import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './Reconciliation.css';


const ReconciliationDetailPage = () => {
  const { businessId, categoryId } = useParams();

  // Existing
  const [categoryName, setCategoryName] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Plaid category match + transactions
  const [plaidCategories, setPlaidCategories] = useState([]);
  const [selectedPlaidCatId, setSelectedPlaidCatId] = useState('');
  const [existingMatchId, setExistingMatchId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isEditingMatch, setIsEditingMatch] = useState(false);

  // Selection + expand/highlight
  const [selectedEntryIds, setSelectedEntryIds] = useState(new Set());
  const [expandedTxId, setExpandedTxId] = useState(null);
  const [txLinkById, setTxLinkById] = useState({}); // { [txId]: { plaid, entries: [...] } }
  const [loadingTxId, setLoadingTxId] = useState({}); // { [txId]: true }
  const [highlightedEntryIds, setHighlightedEntryIds] = useState(new Set());
  const [selectedLinkedTxId, setSelectedLinkedTxId] = useState(null);

  const [deltaByTx, setDeltaByTx] = useState({}); // { [txId]: { linked_sum, delta } }

  // Left-entry -> plaid link + expansion
const [expandedEntryId, setExpandedEntryId] = useState(null);
const [entryLinkById, setEntryLinkById] = useState({}); // { [entryId]: { plaid } }
const [loadingEntryId, setLoadingEntryId] = useState({}); // { [entryId]: true|false }


  // Fetch category name (non-Required Cash page)
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const { data } = await api.get(`/cash-register-category/${categoryId}`);
        setCategoryName(data?.name ?? '');
      } catch (err) {
        console.error('Failed to fetch category', err);
        setError('Could not load category name.');
      }
    };
    if (categoryId) fetchCategory();
  }, [categoryId]);

  // Fetch entries for this cash category
  const loadEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/cash-register-entries-by-category', {
        params: { business_id: businessId, category_id: categoryId }
      });
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch entries', err);
      setError('Could not load entries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId && categoryId) loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, categoryId]);

  // Load Plaid categories + existing match for THIS cash category
  useEffect(() => {
    const run = async () => {
      try {
        const cats = await api.get('/categories', { params: { business_id: businessId } });
        setPlaidCategories(Array.isArray(cats.data) ? cats.data : []);

        const m = await api.get('/cash-category-match', {
          params: { business_id: businessId, cash_category_id: categoryId }
        });
        if (m.data && m.data.plaid_category_id) {
          setExistingMatchId(m.data.id);
          setSelectedPlaidCatId(String(m.data.plaid_category_id));
        }
      } catch (err) {
        console.error('match load error', err);
      }
    };
    if (businessId && categoryId) run();
  }, [businessId, categoryId]);

  // Fetch transactions whenever matched Plaid category changes
  const loadTransactions = async (plaidCatId) => {
  if (!plaidCatId) { setTransactions([]);  return; }
  try {
    const { data } = await api.get('/plaid-transactions', {
      params: { business_id: businessId, category_id: plaidCatId }
    });
    const txs = Array.isArray(data) ? data : [];
    setTransactions(txs);
    await loadDeltas(txs); // <<< fetch deltas for all rows
  } catch (err) {
    console.error('plaid tx fetch error', err);
    setTransactions([]);
  }
};

  



  useEffect(() => {
    loadTransactions(selectedPlaidCatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaidCatId]);

  // Save Plaid match for THIS cash category
  const savePlaidMatch = async () => {
    try {
      if (!selectedPlaidCatId) return;
      if (existingMatchId) {
        await api.put(`/cash-category-match/${existingMatchId}`, {
          business_id: businessId,
          cash_category_id: categoryId,
          plaid_category_id: Number(selectedPlaidCatId)
        });
      } else {
        const r = await api.post('/cash-category-match', {
          business_id: businessId,
          cash_category_id: categoryId,
          plaid_category_id: Number(selectedPlaidCatId)
        });
        setExistingMatchId(r.data?.id ?? null);
      }
      setIsEditingMatch(false);
      await loadTransactions(selectedPlaidCatId);
      alert('Match saved.');
    } catch (err) {
      console.error('savePlaidMatch error', err);
      alert('Failed to save match.');
    }
  };

  // Helpers
  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toISOString().slice(0, 10);
  };
  const fmtMoney = (n) => {
    const val = Number(n);
    return isNaN(val) ? '$0.00' : `$${val.toFixed(2)}`;
  };
  const toggleEntrySelect = (entryId) => {
    setSelectedEntryIds(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };
  const clearSelections = () => setSelectedEntryIds(new Set());


const selectedEntriesTotal = useMemo(() => {
  if (!entries?.length || selectedEntryIds.size === 0) return 0;
  return entries.reduce(
    (sum, e) => (selectedEntryIds.has(e.id) ? sum + Number(e.amount || 0) : sum),
    0
  );
}, [entries, selectedEntryIds]);


  // Find a transaction in the right table using Plaid id or pending id
const findTxByBankId = (bankId) => {
  if (!bankId) return null;
  return transactions.find(
    (t) => String(t.id) === String(bankId) || String(t.pending_transaction_id || '') === String(bankId)
  ) || null;
};



  const loadDeltas = async (txs) => {
  const ids = (txs || []).map(t => t.id);
  if (!ids.length) { setDeltaByTx({}); return; }
  try {
    const { data } = await api.post('/category-reconcile-deltas', {
      business_id: businessId,
      cash_category_id: categoryId,
      plaid_ids: ids
    });
    // Build delta map using returned linked_sum + local tx.amount
    const map = {};
    const byId = Object.fromEntries((txs || []).map(t => [t.id, t]));
    for (const id of ids) {
      const ls = Number(data?.[id]?.linked_sum || 0);
      const amt = Number(byId[id]?.amount || 0);
      map[id] = { linked_sum: ls, delta: -(ls + amt) };
    }
    setDeltaByTx(map);
  } catch (err) {
    console.error('loadDeltas error', err);
    setDeltaByTx({});
  }
};


const toggleExpandEntry = async (entry) => {
  const isSame = expandedEntryId === entry.id;
  const next = isSame ? null : entry.id;
  setExpandedEntryId(next);

  if (!next) return; // collapsed

  // Already resolved once?
  if (entryLinkById[next]?.plaid !== undefined) {
    const plaid = entryLinkById[next].plaid;
    if (plaid) {
      const tx = findTxByBankId(plaid.id) || findTxByBankId(plaid.pending_transaction_id);
      if (tx) { setSelectedLinkedTxId(tx.id); setExpandedTxId(tx.id); }
      else { setSelectedLinkedTxId(null); }
    } else {
      setSelectedLinkedTxId(null);
    }
    return;
  }

  try {
    setLoadingEntryId(prev => ({ ...prev, [next]: true }));

    // 🔎 Only use existing APIs: scan right-table tx links to find the match
    const link = await resolveEntryLinkByScanningTxes(next);

    // Cache result for this entry
    setEntryLinkById(prev => ({ ...prev, [next]: link || { plaid: null } }));

    // Sync highlight/expand on the right
    const plaid = link?.plaid;
    if (plaid) {
      const tx = findTxByBankId(plaid.id) || findTxByBankId(plaid.pending_transaction_id);
      if (tx) { setSelectedLinkedTxId(tx.id); setExpandedTxId(tx.id); }
      else { setSelectedLinkedTxId(null); }
    } else {
      setSelectedLinkedTxId(null);
    }
  } finally {
    setLoadingEntryId(prev => {
      const { [next]: _, ...rest } = prev;
      return rest;
    });
  }
};


  // Expand a transaction row → fetch linked entries and highlight them
  const toggleExpandTx = async (tx) => {
    const isSame = expandedTxId === tx.id;
    const next = isSame ? null : tx.id;
    setExpandedTxId(next);

    if (!next) {
      setHighlightedEntryIds(new Set());
      return;
    }

    if (txLinkById[next]) {
      const ids = (txLinkById[next].entries || []).map(e => e.id);
      setHighlightedEntryIds(new Set(ids));
      setSelectedLinkedTxId(tx.id);
      return;
    }

    try {
      setLoadingTxId(prev => ({ ...prev, [next]: true }));
      // Returns: { plaid: {...}, entries: [{ id, entry_date, amount, is_reconciled }] }
      const { data } = await api.get('/category-reconcile-link-for-tx', {
        params: { business_id: businessId, cash_category_id: categoryId, plaid_id: next }
      });
      setTxLinkById(prev => ({ ...prev, [next]: data || {} }));
      const ids = (data?.entries || []).map(e => e.id);
      setHighlightedEntryIds(new Set(ids));
      setSelectedLinkedTxId(tx.id);
    } catch (err) {
      console.error('toggleExpandTx error', err);
      setHighlightedEntryIds(new Set());
    } finally {
      setLoadingTxId(prev => {
        const { [next]: _, ...rest } = prev;
        return rest;
      });
    }
  };


  // Ensure we have link data for a tx in cache; fetch if missing
const ensureTxLinkCached = async (txId) => {
  if (txLinkById[txId]) return txLinkById[txId];
  try {
    const { data } = await api.get('/category-reconcile-link-for-tx', {
      params: { business_id: businessId, cash_category_id: categoryId, plaid_id: txId }
    });
    setTxLinkById(prev => ({ ...prev, [txId]: data || {} }));
    return data || {};
  } catch (e) {
    return {};
  }
};

// Fallback resolver: find which tx this entry is linked to by scanning tx links
const resolveEntryLinkByScanningTxes = async (entryId) => {
  // 1) check cache first
  for (const [txId, link] of Object.entries(txLinkById || {})) {
    if (Array.isArray(link?.entries) && link.entries.some(e => String(e.id) === String(entryId))) {
      const tx = transactions.find(t => String(t.id) === String(txId));
      return tx ? { plaid: tx } : { plaid: null };
    }
  }
  // 2) fetch per tx until found
  for (const tx of transactions) {
    const link = await ensureTxLinkCached(tx.id);
    if (Array.isArray(link?.entries) && link.entries.some(e => String(e.id) === String(entryId))) {
      return { plaid: tx };
    }
  }
  return { plaid: null };
};


  // Match selected entries → a transaction
  const matchSelectedToTx = async (tx) => {
    const entryIds = Array.from(selectedEntryIds);
    if (!entryIds.length) { alert('Select one or more entries first.'); return; }

    const total = entries
      .filter(e => entryIds.includes(e.id))
      .reduce((s, e) => s + Number(e.amount || 0), 0);

    const txAmount = Number(tx.amount);
    const diff = total + txAmount;
    const statusMsg = diff === 0 ? 'Perfect Match'
      : diff > 0 ? `Short by $${diff.toFixed(2)}`
      : `Over by $${Math.abs(diff).toFixed(2)}`;

    const ok = window.confirm(
      `Selected entries total: ${fmtMoney(total)}\n` +
      `Transaction:          ${fmtMoney(txAmount)}\n` +
      `${statusMsg}\n\nProceed to reconcile?`
    );
    if (!ok) return;

    try {
      await api.post('/category-reconcile-match', {
        business_id: businessId,
        cash_category_id: categoryId,
        plaid_transaction_id: tx.id,
        entry_ids: entryIds,
        total_amount: total,
        plaid_amount: txAmount,
        difference: diff
      });

      // refresh entries + transactions
      await loadEntries();
      await loadTransactions(selectedPlaidCatId);

      // keep tx expanded & refresh its links
      setSelectedLinkedTxId(tx.id);
      setExpandedTxId(tx.id);
      setTxLinkById(prev => { const { [tx.id]: _, ...rest } = prev; return rest; });
      const freshLink = await api.get('/category-reconcile-link-for-tx', {
        params: { business_id: businessId, cash_category_id: categoryId, plaid_id: tx.id }
      });
      setTxLinkById(prev => ({ ...prev, [tx.id]: freshLink.data || {} }));
      const ids = (freshLink.data?.entries || []).map(e => e.id);
      setHighlightedEntryIds(new Set(ids));

      clearSelections();
      alert('Reconciled.');
    } catch (err) {
      console.error('matchSelectedToTx error', err);
      alert('Failed to reconcile.');
    }
  };


  
const formatCatName = (name = "") =>
  name
    .replace(/[_-]+/g, " ")            // underscores/hyphens -> spaces
    .trim()
    .split(/\s+/)
    .map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");


  // Unlink a single entry from a tx
  const unlinkEntryFromTx = async (tx, entry) => {
    const ok = window.confirm(`Unlink ${fmtDate(entry.entry_date)} — ${fmtMoney(entry.amount)} from this transaction?`);
    if (!ok) return;

    try {
      await api.post('/category-reconcile-unlink', {
        business_id: businessId,
        cash_category_id: categoryId,
        plaid_id: tx.id,
        entry_id: entry.id
      });

      await loadEntries();

      // refresh expanded tx details
      setTxLinkById(prev => { const { [tx.id]: _, ...rest } = prev; return rest; });
      const fresh = await api.get('/category-reconcile-link-for-tx', {
        params: { business_id: businessId, cash_category_id: categoryId, plaid_id: tx.id }
      });
      setTxLinkById(prev => ({ ...prev, [tx.id]: fresh.data || {} }));

      const ids = (fresh.data?.entries || []).map(e => e.id);
      setHighlightedEntryIds(new Set(ids));
    } catch (err) {
      console.error('unlinkEntryFromTx error', err);
      alert('Failed to unlink.');
    }
  };

  return (
  <div className='req-cash-page'>
    <h2>Category Reconciliation</h2>

    {/* Top: category title + match selector (kept as-is) */}
    <div>
      <h3 style={{ margin: 0 }}>Reconciliation for {categoryName || '...'}</h3>

      {existingMatchId && !isEditingMatch ? (
        <div className='cat-match-req'>
          <strong>Matched Category:</strong>{' '}
          {plaidCategories.find(c => String(c.id) === String(selectedPlaidCatId))?.name || '(unknown)'}
          <button onClick={() => setIsEditingMatch(true)} style={{ marginLeft: 8 }}>Edit</button>
        </div>
      ) : (
        <div>
          <label>Match to Category: </label>
          <select
            value={selectedPlaidCatId}
            onChange={e => setSelectedPlaidCatId(e.target.value)}
          >
            <option value="">-- Select --</option>
            {plaidCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{formatCatName(cat.name)}</option>
            ))}
          </select>
          <button
            onClick={savePlaidMatch}
            disabled={!selectedPlaidCatId}
            style={{ marginLeft: 8 }}
          >
            Save
          </button>
        </div>
      )}
    </div>

    {error && <div style={{ color: '#b00', marginTop: 8 }}>{error}</div>}

    {/* SIDE-BY-SIDE LAYOUT (like RequiredCashReconciliationPage) */}
    <div className='req-cash-tables'>
      {/* Left: Entries */}
      <div className='req-cash-entry'>
        <h3>Entries</h3>

        {loading ? (
          <div>Loading…</div>
        ) : entries.length === 0 ? (
          <div>No entries found.</div>
        ) : (
          <table className='rc-table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                
              </tr>
            </thead>
           <tbody>
  {entries.map((entry) => {
    const isHighlighted = highlightedEntryIds.has(entry.id);
    const isRec = Number(entry.is_reconciled) === 1;
    const isExpanded = expandedEntryId === entry.id;

    return (
      <React.Fragment key={entry.id}>
        {/* Main row */}
        <tr
          onClick={() => toggleExpandEntry(entry)}
          style={{
            background: isHighlighted ? 'rgba(50,205,50,0.08)' : 'transparent',
            outline: isHighlighted ? '2px solid limegreen' : 'none',
            cursor: 'pointer'
          }}
        >
          <td>
            <input
              type="checkbox"
              checked={selectedEntryIds.has(entry.id)}
              onClick={(e) => e.stopPropagation()}   // <-- prevents toggling expand
              onChange={() => toggleEntrySelect(entry.id)}
            />{' '}
            {fmtDate(entry.entry_date)}{' '}
            
          </td>
          <td>{fmtMoney(entry.amount)}{isRec && <span style={{ marginLeft: 6, color: 'green', fontWeight: 700 }}>✅</span>}</td>
          
        </tr>

        {/* Expanded details row */}
        {isExpanded && (
          <tr>
            {/* colSpan MUST equal your thead column count = 3 */}
            <td colSpan={2} style={{ background: '#fafafa' }}>
              {loadingEntryId[entry.id] ? (
                <div>Loading linked transaction…</div>
              ) : (
                (() => {
                  const link = entryLinkById[entry.id];
                  const plaid = link?.plaid;
                  if (!plaid) {
                    return <div style={{ color: '#a00' }}>No linked transaction found.</div>;
                  }
                  const tx =
                    findTxByBankId(plaid.id) ||
                    findTxByBankId(plaid.pending_transaction_id);

                  return (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <div><strong>Linked Tx:</strong> {plaid.name}</div>
                      <div><strong>Date:</strong> {fmtDate(plaid.date)}</div>
                      <div><strong>Amount:</strong> {fmtMoney(plaid.amount)}</div>
                      {tx && (
                        <button
                          onClick={(e) => { e.stopPropagation(); unlinkEntryFromTx(tx, entry); }}
                          title="Unlink"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b00' }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })()
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  })}
</tbody>

          </table>
          
        )}
        {selectedEntryIds.size > 0 && (
  <div className="rc-selection-summary">
    <div className="rc-selection-summary__left">
      <strong>{selectedEntryIds.size}</strong> {selectedEntryIds.size === 1 ? 'entry' : 'entries'} selected
    </div>
    <div className="rc-selection-summary__right">
      <span>Selected Total:</span>
      <strong>{fmtMoney(selectedEntriesTotal)}</strong>
      <button
        className="rc-clear-btn"
        onClick={() => setSelectedEntryIds(new Set())}
        aria-label="Clear selected entries"
      >
        Clear
      </button>
    </div>
  </div>
)}

      </div>

      {/* Right: Transactions */}
      <div className='req-cash-plaid'>
        <h3>Matched Transactions</h3>

        {transactions.length ? (
          <table className='rc-table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => {
                const isExpanded = expandedTxId === tx.id;
                const isLinked = selectedLinkedTxId === tx.id;
                const isRec = Number(tx.is_reconciled) === 1 || tx.is_reconciled === true;
                const link = txLinkById[tx.id];

                return (
                  <React.Fragment key={tx.id}>
                    <tr
                      onClick={() => toggleExpandTx(tx)}
                      style={{
                        cursor: 'pointer',
                        background: isLinked ? 'rgba(50,205,50,0.08)' : 'transparent',
                        outline: isLinked ? '2px solid limegreen' : 'none'
                      }}
                      title={isLinked ? 'Linked entries highlighted on the left' : ''}
                    >
                      <td>{fmtDate(tx.date)}</td>
                      <td>
                        {tx.name}
                        {isRec && <span style={{ marginLeft: 6, color: 'green', fontWeight: 700 }}>✅</span>}
                      </td>
                      <td>{fmtMoney(tx.amount)}</td>
                      <td>
                        {(() => {
                          const info = deltaByTx[tx.id];
                          if (!info) return '—';
                          const d = info.delta;
                          const isZero = Math.abs(d) < 0.005;
                          const badgeClass = isZero ? 'ok' : (d < 0 ? 'err' : 'ok');
                          const sign = d < 0 ? '-' : '+';
                          return (
                            <span className={`delta-badge ${badgeClass}`}>
                              {isZero ? '$0.00' : `${sign}$${Math.abs(d).toFixed(2)}`}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        {/* IMPORTANT: colSpan must match thead column count = 4 */}
                        <td colSpan={4} style={{ background: '#fafafa' }}>
                          {loadingTxId[tx.id] ? (
                            <div>Loading linked entries…</div>
                          ) : (
                            <>
                              {link && link.entries?.length ? (
                                <div>
                                  <div style={{ marginBottom: 6 }}><strong>Linked entries:</strong></div>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {link.entries.map(e => (
                                      <li key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span>{fmtDate(e.entry_date)} — {fmtMoney(e.amount)}</span>
                                        <button
                                          onClick={(ev) => { ev.stopPropagation(); unlinkEntryFromTx(tx, e); }}
                                          title="Unlink"
                                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b00' }}
                                        >
                                          🗑️
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                  <div style={{ marginTop: 6 }}>
                                    <strong>Total:</strong>{' '}
                                    {fmtMoney(link.entries.reduce((s, e) => s + Number(e.amount || 0), 0))}
                                  </div>
                                </div>
                              ) : (
                                <div style={{ color: '#a00' }}>No entries linked.</div>
                              )}

                              <div style={{ marginTop: 8 }}>
                                <button onClick={(e) => { e.stopPropagation(); matchSelectedToTx(tx); }}>
                                  Match selected entries to this transaction
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); clearSelections(); }} style={{ marginLeft: 8 }}>
                                  Clear entry selection
                                </button>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p>No transactions found.</p>
        )}
      </div>
    </div>
  </div>
);

};

export default ReconciliationDetailPage;


